import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import type {CSSProperties} from 'react';
import Image from 'next/image';
import type {StaticImageData} from 'next/image';
import Container from '@/components/atoms/Container';
import SectionHeader from '@/components/molecules/SectionHeader';
import ArrowButton from '@/components/atoms/ArrowButton';
import {minWidthQuery} from '@/styles/breakpoints';
import {cx} from '@/util/cx';
import styles from './HomeCaseStudyTabs.module.scss';

export interface CaseStudyStat {
    header: string;
    body: string;
}

export interface CaseStudyTab {
    /** Left-rail label: bold line + supporting line. */
    tabTitle: string;
    tabSubtitle: string;
    heading: string;
    desc: string;
    logoSrc: string | StaticImageData;
    logoAlt: string;
    imgSrc: string;
    imgAlt: string;
    /** e.g. `'1155 / 1274'` — matches the real file's aspect ratio. */
    imgAspectRatio?: string;
    stats: CaseStudyStat[];
    redirect: string;
}

interface HomeCaseStudyTabsProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    data: CaseStudyTab[];
}

// Matches `.nav_grid`'s fixed height (src/styles/_header.scss) — the sticky site navbar
// this section docks under. The SCSS module carries the same 64px.
const NAVBAR_HEIGHT = 64;

// Intrinsic size only: the CSS max-width/max-height pair does the real sizing, but
// next/image still needs a ratio up front to reserve space and avoid layout shift.
const CASE_IMAGE_INTRINSIC = {width: 1155, height: 1274} as const;

/** Narrowest width that still runs the scroll-driven tabs; below it the section flows. */
const SCROLL_DRIVEN_MIN_PX = 1198;
const SCROLL_DRIVEN_QUERY = minWidthQuery(SCROLL_DRIVEN_MIN_PX);

// Phones drop the tab rail: the panels become one snap-scrolling track that autoplays.
const AUTOPLAY_MS = 5000;

const HomeCaseStudyTabs = ({eyebrow, heading, subheading, data}: HomeCaseStudyTabsProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    // outerRef: tall scroll container. ScrollTrigger watches it for progress.
    // The inner section sticks via CSS position:sticky — no GSAP pin needed.
    const outerRef = useRef<HTMLDivElement>(null);
    // Mobile carousel: the track scrolls, so `activeIndex` is read back from its
    // scroll position rather than driving it.
    const trackRef = useRef<HTMLDivElement>(null);
    const [paused, setPaused] = useState(false);

    const scrollToSlide = useCallback((index: number) => {
        const track = trackRef.current;
        if (!track) return;
        track.scrollTo({left: index * track.clientWidth, behavior: 'smooth'});
    }, []);

    // Autoplay, mobile only, and only while the track is on screen.
    useEffect(() => {
        const track = trackRef.current;
        if (!track || globalThis.window === undefined || globalThis.innerWidth >= 900) return undefined;

        let visible = false;
        const io = new IntersectionObserver(([entry]) => (visible = entry.isIntersecting), {threshold: 0.3});
        io.observe(track);

        const id = setInterval(() => {
            if (!visible || paused || document.hidden) return;
            const next = (Math.round(track.scrollLeft / track.clientWidth) + 1) % data.length;
            track.scrollTo({left: next * track.clientWidth, behavior: 'smooth'});
        }, AUTOPLAY_MS);

        return () => {
            clearInterval(id);
            io.disconnect();
        };
    }, [data.length, paused]);

    // Keep the dots in step with a manual swipe.
    const handleTrackScroll = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;
        setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
    }, []);

    useEffect(() => {
        // Only wire up scroll-driven tabs where the section actually sticks — the same
        // condition the CSS encodes, subscribed so a resize re-arms or tears down.
        if (globalThis.window === undefined) return undefined;

        const mq = globalThis.matchMedia(SCROLL_DRIVEN_QUERY);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let st: any = null;
        let cancelled = false;

        const teardown = () => {
            st?.kill();
            st = null;
            ro?.disconnect();
            ro = null;
            if (refreshFrame !== undefined) cancelAnimationFrame(refreshFrame);
            refreshFrame = undefined;
        };

        // Sections above are `CvSection`s whose real height lands as they expand, so
        // re-measure the trigger on body resizes.
        let ro: ResizeObserver | null = null;
        let refreshFrame: number | undefined;

        const watchLayoutShifts = (ScrollTrigger: {refresh: () => void}) => {
            if (typeof ResizeObserver === 'undefined') return;
            ro = new ResizeObserver(() => {
                if (refreshFrame !== undefined) return;
                refreshFrame = requestAnimationFrame(() => {
                    refreshFrame = undefined;
                    ScrollTrigger.refresh();
                });
            });
            ro.observe(document.body);
        };

        const init = async () => {
            const {gsap} = await import('gsap');
            const {ScrollTrigger} = await import('gsap/ScrollTrigger');
            if (cancelled || !mq.matches || st || !outerRef.current) return;
            gsap.registerPlugin(ScrollTrigger);

            st = ScrollTrigger.create({
                trigger: outerRef.current,
                start: () => `top top+=${NAVBAR_HEIGHT}`,
                end: 'bottom bottom',
                invalidateOnRefresh: true,
                onUpdate(self) {
                    const idx = Math.min(data.length - 1, Math.floor(self.progress * data.length));
                    setActiveIndex(idx);
                }
            });

            ScrollTrigger.refresh();
            watchLayoutShifts(ScrollTrigger);
        };

        const sync = () => {
            if (mq.matches) {
                if (!st) init();
                return;
            }
            teardown();
            setActiveIndex(0);
        };

        sync();
        mq.addEventListener('change', sync);

        return () => {
            cancelled = true;
            mq.removeEventListener('change', sync);
            teardown();
        };
    }, [data.length]);

    const outerStyle = {'--cs-tab-count': data.length} as CSSProperties;

    return (
        // Tall outer wrapper — provides the scroll distance for each tab.
        <div ref={outerRef} className={styles.outer} style={outerStyle}>
            {/* CSS sticky: section stays in view while the outer container scrolls */}
            <div className={styles.sticky}>
                {/* Decorative glow orbs */}
                <div className={cx(styles.orb, styles.orb1)} />
                <div className={cx(styles.orb, styles.orb2)} />
                <div className={cx(styles.orb, styles.orb3)} />
                <div className={cx(styles.orb, styles.orb4)} />
                <div className={cx(styles.orb, styles.orb5)} />
                <div className={cx(styles.orb, styles.orb6)} />
                <div className={cx(styles.orb, styles.orb7)} />

                <Container className={styles.container}>
                    <div className={styles.headerRow}>
                        <SectionHeader
                            eyebrow={eyebrow}
                            heading={heading}
                            subheading={subheading}
                            align="left"
                            className={styles.headerWrapper}
                            headingClassName={styles.heading}
                            subheadingClassName={styles.subheading}
                        />
                        <div className={styles.viewAllLink}>
                            <ArrowButton
                                variant="outlineLight"
                                shine={false}
                                iconSize="sm"
                                href="/portfolio"
                                target="_blank"
                                className={styles.viewAllBtn}
                            >
                                View All Case Studies
                            </ArrowButton>
                        </div>
                    </div>

                    <div className={styles.grid}>
                        {/* Left rail: one tab per case study */}
                        <div className={cx(styles.gridItem, styles.railCol)}>
                            {data.map((item, index) => {
                                const isActive = activeIndex === index;
                                return (
                                    <button
                                        key={item.redirect}
                                        type="button"
                                        aria-pressed={isActive}
                                        onClick={() => setActiveIndex(index)}
                                        className={cx(styles.tab, isActive && styles.tabActive)}
                                    >
                                        <span className={cx(styles.tabTitle, 'font-headers')}>{item.tabTitle}</span>
                                        <span className={cx(styles.tabSubtitle, 'font-regular-text')}>{item.tabSubtitle}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* All panels are rendered so every case study's copy, stats and link
                            sit in the server HTML for crawlers; only the active one is shown. */}
                        <div className={cx(styles.gridItem, styles.panelCol)}>
                            <div
                                ref={trackRef}
                                className={styles.track}
                                onScroll={handleTrackScroll}
                                onTouchStart={() => setPaused(true)}
                                onTouchEnd={() => setPaused(false)}
                            >
                                {data.map((item, index) => (
                                    <div key={item.redirect} className={cx(styles.slide, activeIndex === index && styles.slideActive)}>
                                        <div className={styles.slideGrid}>
                                            <div className={styles.contentCol}>
                                                <div className={styles.panelContent}>
                                                    {item.logoSrc && (
                                                        <div className={styles.logoWrap}>
                                                            <Image
                                                                src={item.logoSrc}
                                                                alt={item.logoAlt}
                                                                fill
                                                                sizes="140px"
                                                                style={{objectFit: 'contain', objectPosition: 'left center'}}
                                                            />
                                                        </div>
                                                    )}

                                                    <h3 className={cx(styles.caseHeading, 'font-headers')}>{item.heading}</h3>
                                                    <p className={cx(styles.desc, 'font-regular-text')}>{item.desc}</p>

                                                    <div className={styles.statsRow}>
                                                        {item.stats.map((stat) => (
                                                            <div className={styles.statItem} key={stat.header}>
                                                                <div className={styles.statCard}>
                                                                    <span className={cx(styles.statHeader, 'font-headers')}>
                                                                        {stat.header}
                                                                    </span>
                                                                    <span className={cx(styles.statBody, 'font-regular-text')}>
                                                                        {stat.body}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <ArrowButton
                                                        variant="orange"
                                                        iconSize="sm"
                                                        href={item.redirect}
                                                        target="_blank"
                                                        className={styles.ctaBtn}
                                                    >
                                                        View Case Study
                                                    </ArrowButton>
                                                </div>
                                            </div>

                                            <div className={styles.imageCol}>
                                                <div className={styles.caseImagePad}>
                                                    <div className={styles.caseImageWrap}>
                                                        <Image
                                                            src={item.imgSrc}
                                                            alt={item.imgAlt}
                                                            {...CASE_IMAGE_INTRINSIC}
                                                            quality={95}
                                                            sizes="(max-width: 900px) 100vw, 50vw"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Slide indicator — mobile only; the tab rail plays this role on desktop. */}
                            <div className={styles.dotsRow}>
                                {data.map((item, index) => (
                                    <button
                                        key={item.redirect}
                                        type="button"
                                        aria-label={`Show ${item.tabTitle} case study`}
                                        aria-pressed={activeIndex === index}
                                        onClick={() => scrollToSlide(index)}
                                        className={cx(styles.dot, activeIndex === index && styles.dotActive)}
                                    />
                                ))}
                            </div>

                            <div className={styles.viewAllLinkMobile}>
                                <ArrowButton
                                    variant="outlineLight"
                                    shine={false}
                                    iconSize="sm"
                                    href="/portfolio"
                                    target="_blank"
                                    className={styles.viewAllBtn}
                                >
                                    View All Case Studies
                                </ArrowButton>
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
        </div>
    );
};

export default memo(HomeCaseStudyTabs);
